import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { AccessTimeOutlined } from "@mui/icons-material";
import CustomModal from "../../components/Common/CustomModal";
import { CustomBtn, SecondaryBtn } from "../../components/Common/CustomBtn";
import { CustomInput, CustomPasswordInput } from "../../components/Common/CustomInputs";
import {
  ErrorSnackbar,
} from "../../components/Common/CustomSnackbar";
import axiosInstance from "../../utils/config/config";
import {
  ModalBody,
  ModalSubtitle,
  InfoBanner,
  InfoBannerText,
  ModalButtonsRow,
  PasswordRequirements,
  RequirementsTitle,
  RequirementItem,
} from "./Settings.styles";

const STEPS = {
  SEND_CODE: "send-code",
  VERIFY_CODE: "verify-code",
  NEW_PASSWORD: "new-password",
};

const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

const ResetPasswordModal = ({ open, handleClose, onSuccess }) => {
  const { userData } = useSelector((state) => state.user);

  const [step, setStep] = useState(STEPS.SEND_CODE);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });

  const email = userData?.email || "";

  const resetState = useCallback(() => {
    setStep(STEPS.SEND_CODE);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setLoading(false);
    setErrorState({ error: false, message: "" });
  }, []);

  const onClose = useCallback(() => {
    resetState();
    handleClose();
  }, [resetState, handleClose]);

  const closeError = useCallback(
    () => setErrorState({ error: false, message: "" }),
    []
  );

  // Step 1 – send verification code to email
  const handleSendCode = useCallback(async () => {
    try {
      setLoading(true);
      setErrorState({ error: false, message: "" });
      await axiosInstance.post("/user/send-password-code");
      setStep(STEPS.VERIFY_CODE);
    } catch (err) {
      setErrorState({
        error: true,
        message:
          err?.response?.data?.message ||
          "Failed to send verification code. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2 – just move to step 3 (code is verified server-side in step 3)
  const handleVerifyCode = useCallback(() => {
    if (!code.trim()) {
      setErrorState({ error: true, message: "Please enter the verification code." });
      return;
    }
    setErrorState({ error: false, message: "" });
    setStep(STEPS.NEW_PASSWORD);
  }, [code]);

  // Step 3 – submit new password with the code
  const handleResetPassword = useCallback(async () => {
    if (!newPassword || !confirmPassword) {
      setErrorState({ error: true, message: "Please fill in all fields." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorState({ error: true, message: "Passwords do not match." });
      return;
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      setErrorState({
        error: true,
        message:
          "Password must be at least 8 characters with an uppercase letter, a number, and a special character.",
      });
      return;
    }

    try {
      setLoading(true);
      setErrorState({ error: false, message: "" });
      await axiosInstance.post("/user/change-password", {
        code: code.trim(),
        newPassword,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setErrorState({
        error: true,
        message:
          err?.response?.data?.message ||
          "Failed to reset password. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [code, newPassword, confirmPassword, onClose, onSuccess]);

  const renderStepContent = () => {
    switch (step) {
      case STEPS.SEND_CODE:
        return (
          <ModalBody>
            <ModalSubtitle>
              We'll send a verification code to your email
            </ModalSubtitle>
            <Box sx={{ pointerEvents: "none", opacity: 0.7 }}>
              <CustomInput
                label="Email address"
                value={email}
                setValue={() => {}}
                noIcon
                width="100%"
              />
            </Box>
            <ModalButtonsRow>
              <SecondaryBtn text="Cancel" noIcon onClick={onClose} noMt noMb width="auto" />
              <CustomBtn
                text="Send Code"
                noIcon
                onClick={handleSendCode}
                disabled={loading}
                noMt
                noMb
                width="auto"
              />
            </ModalButtonsRow>
          </ModalBody>
        );

      case STEPS.VERIFY_CODE:
        return (
          <ModalBody>
            <ModalSubtitle>Enter the code sent to your email</ModalSubtitle>
            <InfoBanner>
              <AccessTimeOutlined />
              <InfoBannerText>Code sent to {email}</InfoBannerText>
            </InfoBanner>
            <CustomInput
              label="Verification code"
              placeholder="Enter code"
              value={code}
              setValue={setCode}
              noIcon
              width="100%"
            />
            <ModalButtonsRow>
              <SecondaryBtn text="Cancel" noIcon onClick={onClose} noMt noMb width="auto" />
              <CustomBtn
                text="Verify"
                noIcon
                onClick={handleVerifyCode}
                disabled={loading}
                noMt
                noMb
                width="auto"
              />
            </ModalButtonsRow>
          </ModalBody>
        );

      case STEPS.NEW_PASSWORD:
        return (
          <ModalBody>
            <ModalSubtitle>Create a new password</ModalSubtitle>
            <Box sx={{ mb: 2 }}>
              <CustomPasswordInput
                placeholder="Minimum 8 characters"
                value={newPassword}
                setValue={setNewPassword}
                width="100%"
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <CustomPasswordInput
                placeholder="Re-enter password"
                value={confirmPassword}
                setValue={setConfirmPassword}
                width="100%"
              />
            </Box>
            <PasswordRequirements>
              <RequirementsTitle>Password requirements:</RequirementsTitle>
              <RequirementItem>Minimum 8 characters</RequirementItem>
              <RequirementItem>At least one uppercase letter</RequirementItem>
              <RequirementItem>At least one number</RequirementItem>
              <RequirementItem>At least one special character</RequirementItem>
            </PasswordRequirements>
            <ModalButtonsRow>
              <SecondaryBtn text="Cancel" noIcon onClick={onClose} noMt noMb width="auto" />
              <CustomBtn
                text="Reset Password"
                noIcon
                onClick={handleResetPassword}
                disabled={loading}
                noMt
                noMb
                width="auto"
              />
            </ModalButtonsRow>
          </ModalBody>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={closeError}
        errorMsg={errorState.message}
      />
      <CustomModal
        open={open}
        handleClose={onClose}
        title="Reset Password"
        noBtns
        width="480px"
        height="auto"
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              zIndex: 10,
              borderRadius: "30px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {renderStepContent()}
      </CustomModal>
    </>
  );
};

export default ResetPasswordModal;
